import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, InputAdornment, Paper, Grid, Card, CardContent, } from '@mui/material';

const EMICalculator = () => {
  const [formData, setFormData] = useState({
    totalCost: '',
    downPayment: '',
    interestRate: ''
  });

  const [loanAmount, setLoanAmount] = useState(0);
  const [emiOptions, setEmiOptions] = useState({
    years10: 0,
    years20: 0,
    years30: 0
  });

  const handleInputChange = (field) => (event) => {
    let value = event.target.value;
    
    // If field is being cleared, allow empty string
    if (value === '') {
      setFormData(prev => ({
        ...prev,
        [field]: ''
      }));
      return;
    }
    
    // Convert to number
    const numericValue = parseFloat(value);
    
    // Check for invalid numbers
    if (isNaN(numericValue) || numericValue <= 0) {
      return; // Don't update if the value is not a number or is 0/negative
    }
    
    // Special validation for down payment and interest rate (percentage fields)
    if ((field === 'downPayment' || field === 'interestRate') && numericValue > 100) {
      return; // Don't allow values over 100 for percentage fields
    }
    
    // Update the field with the new value
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateEMI = (principal, rate, years) => {
    if (principal <= 0 || rate <= 0 || years <= 0) return 0;

    const monthlyRate = rate / 100 / 12;
    const numberOfPayments = years * 12;

    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

    return Math.round(emi);
  };

  useEffect(() => {
    const totalCost = parseFloat(formData.totalCost) || 0;
    const downPaymentPercentage = parseFloat(formData.downPayment) || 0;
    const interestRate = parseFloat(formData.interestRate) || 0;

    // Convert down payment percentage to monetary amount
    const downPaymentAmount = (downPaymentPercentage / 100) * totalCost;

    const calculatedLoanAmount = totalCost - downPaymentAmount;
    setLoanAmount(calculatedLoanAmount);

    if (calculatedLoanAmount > 0 && interestRate > 0) {
      setEmiOptions({
        years10: calculateEMI(calculatedLoanAmount, interestRate, 10),
        years20: calculateEMI(calculatedLoanAmount, interestRate, 20),
        years30: calculateEMI(calculatedLoanAmount, interestRate, 30)
      });
    } else {
      setEmiOptions({ years10: 0, years20: 0, years30: 0 });
    }
  }, [formData]);


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto', mt: 12 }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#333', fontWeight: 600, mb: 4 }}>
        Home Loan EMI Calculator
      </Typography>

      <Grid container spacing={4}>
        {/* Left Side - Calculator Form */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Total Cost */}
              <Grid item size={{ xs: 12 }}>
                <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
                  Total Cost
                </Typography>
                <TextField size='small' fullWidth placeholder="Enter Total Cost" value={formData.totalCost} onChange={handleInputChange('totalCost')}
                  type="number"
                  inputProps={{
                    inputMode: 'numeric', // This ensures the keyboard is numeric
                    pattern: '[0-9]*', // This allows only numeric input
                    style: { appearance: 'none', MozAppearance: 'textfield', WebkitAppearance: 'none' }, // Removes spinner
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#917376ff',
                      },
                      '&:hover fieldset': {
                        borderColor: '#917376ff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#917376ff',
                      },
                    },
                  }}
                />

              </Grid>

              {/* Down Payment */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>  Down Payment  </Typography>
                <TextField 
                  size='small' 
                  fullWidth 
                  placeholder="Enter Down Payment"
                  value={formData.downPayment} 
                  onChange={handleInputChange('downPayment')}
                  type="number"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    style: { 
                      WebkitAppearance: 'textfield',
                      margin: 0,
                      MozAppearance: 'textfield',
                      appearance: 'textfield'
                    },
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  sx={{
                    '& input[type=number]': {
                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#917376ff',
                      },
                      '&:hover fieldset': {
                        borderColor: '#917376ff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#917376ff',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Interest Rate */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="body1" sx={{ mb: 1, color: '#666' }}> Interest Rate  </Typography>
                <TextField size='small' fullWidth placeholder="Enter Interest Rate"
                  value={formData.interestRate} onChange={handleInputChange('interestRate')}
                  type="number" step="0.1"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{
                    inputMode: 'numeric', // This ensures the keyboard is numeric
                    pattern: '[0-9]*', // This allows only numeric input
                    style: { appearance: 'none', MozAppearance: 'textfield', WebkitAppearance: 'none' }, // Removes spinner
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#917376ff',
                      },
                      '&:hover fieldset': {
                        borderColor: '#917376ff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#917376ff',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Loan Amount Calculation */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>   TOTAL COST - DOWN PAYMENT = LOAN AMOUNT  </Typography>
                  <Typography variant="h6" sx={{ color: '#333', textAlign: 'center', mt: 1 }}> {formatCurrency(loanAmount)} </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right Side - EMI Options */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={2} sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#666', mb: 3 }}>
              Easy Monthly Installments
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card sx={{ bgcolor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#333', mb: 1 }}>
                      For 10 years
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#BB081B', fontWeight: 600 }}>
                      {formatCurrency(emiOptions.years10)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{ bgcolor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#333', mb: 1 }}>
                      For 20 years
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#BB081B', fontWeight: 600 }}>
                      {formatCurrency(emiOptions.years20)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{ bgcolor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#333', mb: 1 }}>
                      For 30 years
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#BB081B', fontWeight: 600 }}>
                      {formatCurrency(emiOptions.years30)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EMICalculator;


// import React, { useState, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   InputAdornment,
//   Paper,
//   Grid,
//   Card,
//   CardContent,
//   Button
// } from '@mui/material';

// const EMICalculator = () => {
//   const [formData, setFormData] = useState({
//     totalCost: '',
//     downPayment: '',
//     interestRate: ''
//   });

//   const [loanAmount, setLoanAmount] = useState(0);
//   const [emiOptions, setEmiOptions] = useState({
//     years10: 0,
//     years20: 0,
//     years30: 0
//   });

//   const handleInputChange = (field) => (event) => {
//     const value = event.target.value;
//     setFormData(prev => ({
//       ...prev,
//       [field]: value
//     }));
//   };

//   const calculateEMI = (principal, rate, years) => {
//     if (principal <= 0 || rate <= 0 || years <= 0) return 0;

//     const monthlyRate = rate / 100 / 12;
//     const numberOfPayments = years * 12;

//     const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
//       (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

//     return Math.round(emi);
//   };

//   useEffect(() => {
//     const totalCost = parseFloat(formData.totalCost) || 0;
//     const downPaymentPercentage = parseFloat(formData.downPayment) || 0;
//     const interestRate = parseFloat(formData.interestRate) || 0;

//     // Convert down payment percentage to monetary amount
//     const downPaymentAmount = (downPaymentPercentage / 100) * totalCost;

//     const calculatedLoanAmount = totalCost - downPaymentAmount;
//     setLoanAmount(calculatedLoanAmount);

//     if (calculatedLoanAmount > 0 && interestRate > 0) {
//       setEmiOptions({
//         years10: calculateEMI(calculatedLoanAmount, interestRate, 10),
//         years20: calculateEMI(calculatedLoanAmount, interestRate, 20),
//         years30: calculateEMI(calculatedLoanAmount, interestRate, 30)
//       });
//     } else {
//       setEmiOptions({ years10: 0, years20: 0, years30: 0 });
//     }
//   }, [formData]);


//   const formatCurrency = (amount) => {
//     return new Intl.NumberFormat('en-IN', {
//       style: 'currency',
//       currency: 'INR',
//       minimumFractionDigits: 0,
//       maximumFractionDigits: 0
//     }).format(amount);
//   };

//   return (
//     <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto', mt: 12 }}>
//       <Typography variant="h4" gutterBottom sx={{ color: '#333', fontWeight: 600, mb: 4 }}>
//         Home Loan EMI Calculator
//       </Typography>

//       <Grid container spacing={4}>
//         {/* Left Side - Calculator Form */}
//         <Grid size={{ xs: 12, md: 6 }}>
//           <Paper elevation={2} sx={{ p: 3 }}>
//             <Grid container spacing={3}>
//               {/* Total Cost */}
//               <Grid item size={{ xs: 12 }}>
//                 <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
//                   Total Cost
//                 </Typography>
//                 <TextField
//                   size='small'
//                   fullWidth
//                   placeholder="Enter Total Cost"
//                   value={formData.totalCost}
//                   onChange={handleInputChange('totalCost')}
//                   type="number"
//                   disabled // Disable the text field
//                   inputProps={{
//                     inputMode: 'numeric', // This ensures the keyboard is numeric
//                     pattern: '[0-9]*', // This allows only numeric input
//                     style: { appearance: 'none', MozAppearance: 'textfield', WebkitAppearance: 'none' }, // Removes spinner
//                   }}
//                   InputProps={{
//                     startAdornment: <InputAdornment position="start">₹</InputAdornment>,
//                   }}
//                   sx={{
//                     '& .MuiOutlinedInput-root': {
//                       '& fieldset': {
//                         borderColor: '#917376ff',
//                       },
//                       '&:hover fieldset': {
//                         borderColor: '#917376ff',
//                       },
//                       '&.Mui-focused fieldset': {
//                         borderColor: '#917376ff',
//                       },
//                     },
//                   }}
//                 />
//               </Grid>

//               {/* Down Payment */}
//               <Grid size={{ xs: 12 }}>
//                 <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
//                   Down Payment
//                 </Typography>
//                 <TextField size='small'
//                   fullWidth
//                   placeholder="Enter Down Payment"
//                   value={formData.downPayment}
//                   onChange={handleInputChange('downPayment')}
//                   type="number"
//                   disabled // Disable the text field
//                   inputProps={{
//                     inputMode: 'numeric', // This ensures the keyboard is numeric
//                     pattern: '[0-9]*', // This allows only numeric input
//                     style: { appearance: 'none', MozAppearance: 'textfield', WebkitAppearance: 'none' }, // Removes spinner
//                   }}
//                   InputProps={{
//                     endAdornment: <InputAdornment position="end">%</InputAdornment>,
//                   }}
//                   sx={{
//                     '& .MuiOutlinedInput-root': {
//                       '& fieldset': {
//                         borderColor: '#917376ff',
//                       },
//                       '&:hover fieldset': {
//                         borderColor: '#917376ff',
//                       },
//                       '&.Mui-focused fieldset': {
//                         borderColor: '#917376ff',
//                       },
//                     },
//                   }}
//                 />
//               </Grid>

//               {/* Interest Rate */}
//               <Grid size={{ xs: 12 }}>
//                 <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
//                   Interest Rate
//                 </Typography>
//                 <TextField
//                   size='small' fullWidth placeholder="Enter Interest Rate"
//                   value={formData.interestRate}
//                   onChange={handleInputChange('interestRate')}
//                   type="number" step="0.1"
//                   disabled // Disable the text field
//                   InputProps={{
//                     endAdornment: <InputAdornment position="end">%</InputAdornment>,
//                   }}
//                   inputProps={{
//                     inputMode: 'numeric', // This ensures the keyboard is numeric
//                     pattern: '[0-9]*', // This allows only numeric input
//                     style: { appearance: 'none', MozAppearance: 'textfield', WebkitAppearance: 'none' }, // Removes spinner
//                   }}
//                   sx={{
//                     '& .MuiOutlinedInput-root': {
//                       '& fieldset': {
//                         borderColor: '#917376ff',
//                       },
//                       '&:hover fieldset': {
//                         borderColor: '#917376ff',
//                       },
//                       '&.Mui-focused fieldset': {
//                         borderColor: '#917376ff',
//                       },
//                     },
//                   }}
//                 />
//               </Grid>

//               {/* Loan Amount Calculation */}
//               <Grid size={{ xs: 12 }}>
//                 <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
//                   <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
//                     TOTAL COST - DOWN PAYMENT = LOAN AMOUNT
//                   </Typography>
//                   <Typography variant="h6" sx={{ color: '#333', textAlign: 'center', mt: 1 }}>
//                     {formatCurrency(loanAmount)}
//                   </Typography>
//                 </Box>
//               </Grid>
//             </Grid>
//           </Paper>
//         </Grid>

//         {/* Right Side - EMI Options */}
//         <Grid  size={{xs:12,md:5}} >
//           <Paper elevation={2} sx={{ p: 3, height: 'fit-content' }}>
//             <Typography variant="h6" gutterBottom sx={{ color: '#666', mb: 3 }}>
//               Easy Monthly Installments
//             </Typography>

//             <Grid container spacing={2}>
//               <Grid  size={{xs:12}}>
//                 <Card sx={{ bgcolor: '#f8f9fa', border: '1px solid #e9ecef' }}>
//                   <CardContent>
//                     <Typography variant="h6" sx={{ color: '#333', mb: 1 }}>
//                       For 10 years
//                     </Typography>
//                     <Typography variant="h5" sx={{ color: '#BB081B', fontWeight: 600 }}>
//                       {formatCurrency(emiOptions.years10)}
//                     </Typography>
//                   </CardContent>
//                 </Card>
//               </Grid>

//               <Grid  size={{xs:12}}>
//                 <Card sx={{ bgcolor: '#f8f9fa', border: '1px solid #e9ecef' }}>
//                   <CardContent>
//                     <Typography variant="h6" sx={{ color: '#333', mb: 1 }}>
//                       For 20 years
//                     </Typography>
//                     <Typography variant="h5" sx={{ color: '#BB081B', fontWeight: 600 }}>
//                       {formatCurrency(emiOptions.years20)}
//                     </Typography>
//                   </CardContent>
//                 </Card>
//               </Grid>

//               <Grid  size={{xs:12}}>
//                 <Card sx={{ bgcolor: '#f8f9fa', border: '1px solid #e9ecef' }}>
//                   <CardContent>
//                     <Typography variant="h6" sx={{ color: '#333', mb: 1 }}>
//                       For 30 years
//                     </Typography>
//                     <Typography variant="h5" sx={{ color: '#BB081B', fontWeight: 600 }}>
//                       {formatCurrency(emiOptions.years30)}
//                     </Typography>
//                   </CardContent>
//                 </Card>
//               </Grid>
//             </Grid>
//           </Paper>
//         </Grid>
//       </Grid>
//     </Box>
//   );
// };

// export default EMICalculator;
